/*
Copyright 2026 Element Creations Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { useTranslation } from "react-i18next";
import { type FC, type JSX, type Ref, useMemo, useState } from "react";
import classNames from "classnames";

import LogoMark from "../icons/LogoMark.svg?react";
import LogoType from "../icons/LogoType.svg?react";
import {
  EndCallButton,
  MicButton,
  VideoButton,
  ShareScreenButton,
  RecordButton,
  CaptionsButton,
  ModeratorIconButton,
  ReactionToggleButton,
  LoudspeakerButton,
  SettingsIconButton,
  type ReactionData,
} from "../button";
import styles from "./CallFooter.module.css";
import { MoreButton } from "./MoreButton";
import { RecordModal } from "./RecordModal";
import { TranscriptModal } from "./TranscriptModal";
import { type RecordingOptions } from "../state/Recording";
import {
  MediaMuteAndSwitchButton,
  type MenuOptions,
} from "./MediaMuteAndSwitchButton";
import { type Behavior } from "../state/Behavior";
import { type ViewModel } from "../state/ViewModel";
import { useBehavior } from "../useBehavior";
import { type LayoutSwitchViewModel } from "../state/LayoutSwitchViewModel";
import { LayoutSwitch } from "../room/LayoutSwitch";

export interface AudioOutputSwitcher {
  targetOutput: string;
  switch: () => void;
}

/**
 * The Snapshot combines all fields required to populate the view.
 *
 * It is a combination of Actions and State.
 * All Actions and State will be wrappen in behaviors.
 * This has the advantage, that actions can mutate.
 * (example: a device gets disconnected, the swicht action is not possible anymore, the actions becomes undefined)
 * With it being reactive we can use the existance of the action to update the rendering without
 * requiring additional state.
 *
 * Comment: It might not make sense to seperate the two interfaces. Hence the seperation
 * just happens on the syntax level with the `type = ... & ...` notation.
 */
export type FooterSnapshot = FooterActions & FooterState;
export interface FooterActions {
  /** Also controls if the audioMute button is disabled */
  toggleAudio: (() => void) | undefined;
  /** Also controls if the videoMute button is disabled */
  toggleVideo: (() => void) | undefined;
  toggleBlur: (() => void) | undefined;
  toggleScreenSharing: (() => void) | undefined;
  /** Starts or stops recording; also controls if the record button is visible */
  toggleRecording: ((options?: RecordingOptions) => void) | undefined;
  /** Turns captions on or off; also controls if the captions button is visible */
  toggleCaptions: (() => void) | undefined;
  /** Also controls if the settings button is visible */
  openSettings: (() => void) | undefined;
  /** Set for moderators, who get a choice of settings from the button */
  openModeratorSettings: (() => void) | undefined;
  /** Also controls if the hangup button is visible */
  hangup: (() => void) | undefined;
}
// we do not use any ? optional properties so that the vm type is including all fields.
export interface FooterState {
  audioEnabled: boolean;
  audioBusy: boolean;
  /** Whether a moderator has muted our microphone, disabling the button */
  audioMutedByModerator: boolean;
  videoEnabled: boolean;
  videoBusy: boolean;
  videoBlurEnabled: boolean;
  showFooter: boolean;

  /* This is needed for WindowMode = "flat" */
  hideControls: boolean;
  /** The footer should be used as an overlay.
   * (Over the Call Grid) This saves spaces on small screens. */
  asOverlay: boolean;
  showModals: boolean;

  buttonSize: "md" | "lg";
  showLogo: boolean;

  /** Also controls if the layout switch is visible */
  layoutSwitchVm: LayoutSwitchViewModel | null;

  sharingScreen: boolean;
  recording: boolean;
  /** Whether the recorder has yet to confirm that it's recording */
  recordingPending: boolean;
  /** Whether this device is showing captions */
  captions: boolean;
  /** Whether a transcript of the call is being kept in the room */
  transcript: boolean;
  /** Starts or stops the transcript */
  setTranscript: ((keep: boolean) => void) | undefined;

  /** Also controls if the audio output button is visible */
  audioOutputSwitcher: AudioOutputSwitcher | undefined;

  reactionIdentifier: string | undefined;
  reactionData: ReactionData | undefined;

  // debug stuff
  debugTileLayout: boolean;
  tileStoreGeneration: number | undefined;

  /** Providing no options `[]` or `undefined` will imply that we dont have a audio fast switcher */
  audioOptions: MenuOptions[];
  /** Providing no options `[]` or `undefined` will imply that we dont have a audio fast switcher */
  videoOptions: MenuOptions[];
  selectedAudio: string | undefined;
  selectedVideo: string | undefined;
  selectAudioButtonOption: ((deviceId: string) => void) | undefined;
  selectVideoButtonOption: ((option: string) => void) | undefined;
}

export interface FooterProps {
  className?: string;
  ref?: Ref<HTMLDivElement>;
  children?: JSX.Element | JSX.Element[] | false;
  vm: ViewModel<FooterSnapshot>;
}
export const CallFooter: FC<FooterProps> = ({
  className,
  ref,
  children,
  vm,
}) => {
  const { t } = useTranslation();
  const asOverlay = useBehavior(vm.asOverlay$);
  const showFooter = useBehavior(vm.showFooter$);
  const hideControls = useBehavior(vm.hideControls$);
  const showModals = useBehavior(vm.showModals$);
  const layoutSwitchVm = useBehavior(vm.layoutSwitchVm$);
  const openSettings = useBehavior(vm.openSettings$);
  const openModeratorSettings = useBehavior(vm.openModeratorSettings$);
  const audioEnabled = useBehavior(vm.audioEnabled$);
  const audioBusy = useBehavior(vm.audioBusy$);
  const audioMutedByModerator = useBehavior(vm.audioMutedByModerator$);
  const videoEnabled = useBehavior(vm.videoEnabled$);
  const videoBusy = useBehavior(vm.videoBusy$);
  const audioDisabledReason = audioMutedByModerator
    ? t("moderation.audio_muted_by_moderator")
    : undefined;
  const toggleAudio = useBehavior(vm.toggleAudio$);
  const toggleVideo = useBehavior(vm.toggleVideo$);
  const sharingScreen = useBehavior(vm.sharingScreen$);
  const toggleScreenSharing = useBehavior(vm.toggleScreenSharing$);
  const recording = useBehavior(vm.recording$);
  const recordingPending = useBehavior(vm.recordingPending$);
  const toggleRecording = useBehavior(vm.toggleRecording$);
  const captions = useBehavior(vm.captions$);
  const toggleCaptions = useBehavior(vm.toggleCaptions$);
  const transcript = useBehavior(vm.transcript$);
  const setTranscript = useBehavior(vm.setTranscript$);
  // Captions are ours alone; whether the room keeps a transcript is asked
  // as they're turned on, and whether it carries on as they're turned off
  const [transcriptModalOpen, setTranscriptModalOpen] = useState(false);
  const onCaptionsClick = useMemo(
    () =>
      toggleCaptions === undefined
        ? undefined
        : (): void => {
            toggleCaptions();
            // On with no transcript yet, or off with one running
            if (setTranscript !== undefined && captions === transcript)
              setTranscriptModalOpen(true);
          },
    [toggleCaptions, setTranscript, captions, transcript],
  );
  // Starting a recording warns the whole room, so it's confirmed first
  const [recordModalOpen, setRecordModalOpen] = useState(false);
  const onRecordClick = useMemo(
    () =>
      toggleRecording === undefined
        ? undefined
        : (): void => {
            if (recording) toggleRecording();
            else setRecordModalOpen(true);
          },
    [toggleRecording, recording],
  );
  const reactionIdentifier = useBehavior(vm.reactionIdentifier$);
  const reactionData = useBehavior(vm.reactionData$);
  const audioOutputSwitcher = useBehavior(vm.audioOutputSwitcher$);
  const hangup = useBehavior(vm.hangup$);
  const debugTileLayout = useBehavior(vm.debugTileLayout$);
  const videoOptions = useBehavior(vm.videoOptions$);
  const selectedVideo = useBehavior(vm.selectedVideo$);
  const audioOptions = useBehavior(vm.audioOptions$);
  const selectedAudio = useBehavior(vm.selectedAudio$);
  const selectAudioButtonOption = useBehavior(vm.selectAudioButtonOption$);
  const selectVideoButtonOption = useBehavior(vm.selectVideoButtonOption$);
  const toggleBlur = useBehavior(vm.toggleBlur$);
  const videoBlurEnabled = useBehavior(vm.videoBlurEnabled$);
  const buttonSize = useBehavior(vm.buttonSize$);
  const showLogo = useBehavior(vm.showLogo$);

  const buttons: JSX.Element[] = [];

  if ((audioOptions?.length ?? 0) > 0) {
    buttons.push(
      <MediaMuteAndSwitchButton
        title={"Mic Source"}
        key="audio"
        iconsAndLabels="audio"
        enabled={audioEnabled ?? false}
        busy={audioBusy ?? false}
        onMuteClick={toggleAudio}
        muteDisabledReason={audioDisabledReason}
        data-testid="incall_mute"
        options={audioOptions}
        selectedOption={selectedAudio}
        onSelect={selectAudioButtonOption}
      />,
    );
  } else {
    buttons.push(
      <MicButton
        size={buttonSize}
        key="audio"
        enabled={audioEnabled ?? false}
        busy={audioBusy ?? false}
        onClick={toggleAudio}
        disabled={(audioBusy ?? false) || toggleAudio === undefined}
        disabledReason={audioDisabledReason}
        data-testid="incall_mute"
      />,
    );
  }

  if ((videoOptions?.length ?? 0) > 0) {
    buttons.push(
      <MediaMuteAndSwitchButton
        title={"Camera Source"}
        key="video"
        iconsAndLabels="video"
        enabled={videoEnabled ?? false}
        busy={videoBusy ?? false}
        onMuteClick={toggleVideo}
        options={videoOptions}
        selectedOption={selectedVideo}
        onSelect={selectVideoButtonOption}
        videoBlurToggleClick={toggleBlur}
        videoBlurEnabled={videoBlurEnabled}
      />,
    );
  } else {
    buttons.push(
      <VideoButton
        size={buttonSize}
        key="video"
        enabled={videoEnabled ?? false}
        busy={videoBusy ?? false}
        onClick={toggleVideo}
        disabled={(videoBusy ?? false) || toggleVideo === undefined}
        data-testid="incall_videomute"
      />,
    );
  }

  if (toggleScreenSharing !== undefined) {
    buttons.push(
      <ShareScreenButton
        size={buttonSize}
        key="share_screen"
        className={styles.shareScreen}
        enabled={sharingScreen ?? false}
        onClick={toggleScreenSharing}
        data-testid="incall_screenshare"
      />,
    );
  }

  if (toggleRecording !== undefined) {
    buttons.push(
      <RecordButton
        size={buttonSize}
        key="record"
        className={styles.record}
        recording={recording}
        pending={recordingPending}
        onClick={onRecordClick}
        data-testid="incall_record"
      />,
    );
  }

  if (onCaptionsClick !== undefined) {
    buttons.push(
      <CaptionsButton
        size={buttonSize}
        key="captions"
        className={styles.captions}
        enabled={captions}
        onClick={onCaptionsClick}
        data-testid="incall_captions"
      />,
    );
  }

  // Reaction button contains a pretty large menu, so treat it like a modal
  if (reactionIdentifier && reactionData && showModals) {
    buttons.push(
      <ReactionToggleButton
        size={buttonSize}
        reactionData={reactionData}
        key="raise_hand"
        className={styles.raiseHand}
        identifier={reactionIdentifier}
      />,
    );
  }

  // In this PR we just move the button to the bottom bar. We do not yet update its appearance
  const audioOutputButton = useMemo(() => {
    if (audioOutputSwitcher === undefined) return null;
    return (
      <LoudspeakerButton
        size={buttonSize}
        onClick={() => audioOutputSwitcher.switch()}
        loudspeakerModeEnabled={audioOutputSwitcher.targetOutput === "earpiece"}
      />
    );
  }, [audioOutputSwitcher, buttonSize]);

  if (audioOutputButton) buttons.push(audioOutputButton);

  // Narrow footers hide settings, screen sharing, recording and reactions
  // behind this menu instead (see CallFooter.module.css)
  buttons.push(
    <MoreButton
      key="more"
      size={buttonSize}
      className={styles.more}
      openSettings={openSettings}
      openModeratorSettings={openModeratorSettings}
      sharingScreen={sharingScreen}
      toggleScreenSharing={toggleScreenSharing}
      recording={recording}
      toggleRecording={onRecordClick}
      captions={captions}
      toggleCaptions={onCaptionsClick}
      reactions={
        reactionIdentifier && reactionData && showModals
          ? { identifier: reactionIdentifier, reactionData }
          : undefined
      }
    />,
  );

  if (hangup)
    buttons.push(
      <EndCallButton
        size={buttonSize}
        key="end_call"
        onClick={hangup}
        data-testid="incall_leave"
      />,
    );

  const logoDebugContainer = (
    <div className={styles.logo}>
      {showLogo && (
        <>
          <LogoMark width={24} height={24} aria-hidden />
          <LogoType
            width={80}
            height={11}
            aria-label={import.meta.env.VITE_PRODUCT_NAME || "Element Call"}
          />
        </>
      )}
      {debugTileLayout ? (
        <TilesDebugInfo generation$={vm.tileStoreGeneration$} />
      ) : undefined}
    </div>
  );

  return (
    <div
      ref={ref}
      data-testid="footer-container"
      className={classNames(className, styles.footer, {
        [styles.overlay]: asOverlay,
        [styles.hidden]: !showFooter,
      })}
    >
      <div className={styles.settingsLogoContainer}>
        {openSettings !== undefined && (
          <SettingsIconButton
            kind="secondary"
            showForScreenWidth="wide"
            data-testid="settings-bottom-left"
            onClick={openSettings}
          />
        )}
        {openSettings !== undefined && openModeratorSettings !== undefined && (
          <ModeratorIconButton
            kind="secondary"
            showForScreenWidth="wide"
            onClick={openModeratorSettings}
          />
        )}
        {children}
        {(showLogo || debugTileLayout) && logoDebugContainer}
      </div>
      {!hideControls && <div className={styles.buttons}>{buttons}</div>}
      {!hideControls && layoutSwitchVm && (
        <LayoutSwitch vm={layoutSwitchVm} className={styles.layout} />
      )}
      {setTranscript !== undefined && transcriptModalOpen && (
        <TranscriptModal
          open
          keeping={transcript}
          onDismiss={() => setTranscriptModalOpen(false)}
          onChoose={(keep) => {
            setTranscriptModalOpen(false);
            if (keep !== transcript) setTranscript(keep);
          }}
        />
      )}
      {toggleRecording !== undefined && recordModalOpen && (
        <RecordModal
          open
          onDismiss={() => setRecordModalOpen(false)}
          onStart={(options) => {
            setRecordModalOpen(false);
            toggleRecording(options);
          }}
        />
      )}
    </div>
  );
};

interface TilesDebugInfoProps {
  generation$: Behavior<number | undefined>;
}

// Isolated in its own component since the layout generation updates frequently
// and we can avoid re-rendering the footer this way
const TilesDebugInfo: FC<TilesDebugInfoProps> = ({ generation$ }) => {
  const generation = useBehavior(generation$);
  return `Tiles generation: ${generation}`;
};
